import { useState, useRef, useCallback } from "react";
import { cents } from "byzantine";
import filters from "byzantine/src/filters";
import type { BasePaymentRowType } from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";

type RowActions = {
  addRow: () => void;
  removeRows: () => void;
  toggleRowSelection: (index: number) => void;
  toggleAllRows: (toggleOn: boolean) => void;
  selectedRows: number[];
  duplicateRow: () => void;
  clearRowsSelection: () => void;
};

const useBulkTableState = (rowActions: RowActions) => {
  const eligibilityCacheRef = useRef<Record<string, boolean>>({});
  // local state
  const [checkboxHeaderSelected, setCheckboxHeaderSelected] = useState(false);
  const [postingDialogIsOpen, setPostingDialogIsOpen] = useState(false);

  const {
    addRow,
    removeRows,
    toggleRowSelection,
    toggleAllRows,
    selectedRows,
    duplicateRow,
    clearRowsSelection,
  } = rowActions;

  const handleClearRowsSelection = useCallback(() => {
    clearRowsSelection();
    setCheckboxHeaderSelected(false);
  }, [clearRowsSelection]);

  const handleToggleCheckboxHeader = useCallback(() => {
    toggleAllRows(!checkboxHeaderSelected);
    setCheckboxHeaderSelected(!checkboxHeaderSelected);
  }, [toggleAllRows, checkboxHeaderSelected]);

  const handleDuplicateRow = useCallback(() => {
    setCheckboxHeaderSelected(false);
    duplicateRow();
    clearRowsSelection();
  }, [duplicateRow, clearRowsSelection]);

  const handleRemoveRows = useCallback(() => {
    setCheckboxHeaderSelected(false);
    removeRows();
  }, [removeRows]);

  const calculateTotalAmount = useCallback(
    (selectedIndices: number[], payments: BasePaymentRowType[]) => {
      const totalCents = selectedIndices.reduce((sum, index) => {
        const amount = payments[index]?.amount || 0;
        return sum + amount;
      }, 0);
      return filters.currency(cents(totalCents));
    },
    []
  );

  const togglePostingDialog = useCallback(() => {
    setPostingDialogIsOpen((prev) => !prev);
  }, []);

  return {
    // checkbox state
    checkboxHeaderSelected,
    handleToggleCheckboxHeader,
    // row actions
    addRow,
    selectedRows,
    toggleRowSelection,
    handleClearRowsSelection,
    handleDuplicateRow,
    handleRemoveRows,
    calculateTotalAmount,
    // posting dialog
    postingDialogIsOpen,
    togglePostingDialog,
    eligibilityCacheRef,
  };
};

export default useBulkTableState;
